import {
    AsyncOperationId,
    asyncOperationPercentComplete,
    AsyncOperationStatus,
    isAsyncOperationTerminal,
} from "@sp-crm/core";
import { useMutation, useQuery } from "@tanstack/react-query";
import { trpc } from "components/rpc/trpc";
import { useCallback } from "react";
import { AsyncOperationHandleView } from "./types";

export const standardAsyncOperationPollIntervalMs = 1000;

export interface UseAsyncOperationResult {
    handle: AsyncOperationHandleView | null;
    isRunning: boolean;
    isComplete: boolean;
    isError: boolean;
    percentComplete: number | null;
    cancel: () => void;
    isCanceling: boolean;
}

export const useAsyncOperation = (
    operationId: AsyncOperationId | null,
    pollIntervalMs: number,
): UseAsyncOperationResult => {
    const trpcClient = trpc.useTRPC();

    const query = useQuery({
        ...trpcClient.asyncOperation.get.queryOptions({ id: operationId }),
        enabled: operationId !== null,
        refetchInterval: q => {
            if (q.state.status === "error") {
                return false;
            }
            const handle = q.state.data as AsyncOperationHandleView | undefined;
            if (!handle) {
                return pollIntervalMs;
            }
            return isAsyncOperationTerminal(handle.status) ? false : pollIntervalMs;
        },
    });

    const cancelMutation = useMutation({
        ...trpcClient.asyncOperation.cancel.mutationOptions(),
        onSuccess: () => query.refetch(),
    });

    const cancel = useCallback(() => {
        if (operationId === null) {
            return;
        }
        cancelMutation.mutate({ id: operationId });
    }, [operationId, cancelMutation]);

    const handle = (query.data as AsyncOperationHandleView | undefined) ?? null;

    return {
        handle,
        isRunning: handle !== null && !isAsyncOperationTerminal(handle.status),
        isComplete: handle?.status === AsyncOperationStatus.Succeeded,
        isError: query.isError,
        percentComplete: handle
            ? asyncOperationPercentComplete({
                  completed: handle.progress.completed,
                  total: handle.progress.total ?? null,
                  message: handle.progress.message ?? null,
              })
            : null,
        cancel,
        isCanceling: cancelMutation.isPending,
    };
};
